import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { Plus, Loader, Calendar, ChevronLeft, ChevronRight, Save, Edit2, Trash2 } from 'lucide-react';

const Expenses = () => {
  // Form state
  const [date, setDate] = useState(() => new Date().toLocaleDateString('en-CA'));
  const [zongLoad, setZongLoad] = useState('');
  const [connectLoad, setConnectLoad] = useState('');
  const [fibrishLoad, setFibrishLoad] = useState('');
  const [ascontelLoad, setAscontelLoad] = useState('');
  const [officeExpense, setOfficeExpense] = useState('');
  const [officeTotal, setOfficeTotal] = useState('');
  const [stock, setStock] = useState('');
  const [stockTotal, setStockTotal] = useState('');
  const [extra, setExtra] = useState('');
  const [extraTotal, setExtraTotal] = useState('');
  const [abdulRehmanPayment, setAbdulRehmanPayment] = useState('');
  const [adilMushtaqPayment, setAdilMushtaqPayment] = useState('');
  const [usmanYousafPayment, setUsmanYousafPayment] = useState('');
  const [notes, setNotes] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Data state
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const [summary, setSummary] = useState({
    zong_load_total: 0,
    connect_load_total: 0,
    fibrish_load_total: 0,
    ascontel_load_total: 0,
    panel_load_total: 0,
    today_load_total: 0,
    office_total: 0,
    stock_total: 0,
    extra_total: 0,
    partner_payments_total: 0,
    grand_total: 0,
    entry_count: 0,
    partner_limits: {},
  });

  const monthStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-01`;

  const fetchEntries = useCallback(async () => {
    try {
      setLoading(true);
      const [entriesRes, summaryRes] = await Promise.all([
        api.get('/expenses/', { params: { month: monthStr } }),
        api.get('/expenses/summary/', { params: { month: monthStr } }),
      ]);
      setEntries(entriesRes.data.results || entriesRes.data || []);
      setSummary(summaryRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [monthStr]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const todayPanelLoadTotal =
    (parseFloat(zongLoad) || 0) +
    (parseFloat(connectLoad) || 0) +
    (parseFloat(fibrishLoad) || 0) +
    (parseFloat(ascontelLoad) || 0);

  const resetForm = () => {
    setDate(new Date().toLocaleDateString('en-CA'));
    setZongLoad('');
    setConnectLoad('');
    setFibrishLoad('');
    setAscontelLoad('');
    setOfficeExpense('');
    setOfficeTotal('');
    setStock('');
    setStockTotal('');
    setExtra('');
    setExtraTotal('');
    setAbdulRehmanPayment('');
    setAdilMushtaqPayment('');
    setUsmanYousafPayment('');
    setNotes('');
    setEditingId(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      const payload = {
        date,
        zong_load: parseFloat(zongLoad) || 0,
        connect_load: parseFloat(connectLoad) || 0,
        fibrish_load: parseFloat(fibrishLoad) || 0,
        ascontel_load: parseFloat(ascontelLoad) || 0,
        office_expense: officeExpense.trim(),
        office_total: parseFloat(officeTotal) || 0,
        stock: stock.trim(),
        stock_total: parseFloat(stockTotal) || 0,
        extra: extra.trim(),
        extra_total: parseFloat(extraTotal) || 0,
        abdul_rehman_payment: parseFloat(abdulRehmanPayment) || 0,
        adil_mushtaq_payment: parseFloat(adilMushtaqPayment) || 0,
        usman_yousaf_payment: parseFloat(usmanYousafPayment) || 0,
        notes: notes.trim(),
      };

      if (editingId) {
        await api.patch(`/expenses/${editingId}/`, payload);
      } else {
        await api.post('/expenses/', payload);
      }

      resetForm();
      await fetchEntries();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.date?.[0] || 'Failed to save entry');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (entry) => {
    setDate(entry.date);
    setZongLoad(entry.zong_load?.toString() || entry.panel_load?.toString() || '');
    setConnectLoad(entry.connect_load?.toString() || '');
    setFibrishLoad(entry.fibrish_load?.toString() || '');
    setAscontelLoad(entry.ascontel_load?.toString() || '');
    setOfficeExpense(entry.office_expense || '');
    setOfficeTotal(entry.office_total?.toString() || '');
    setStock(entry.stock || '');
    setStockTotal(entry.stock_total?.toString() || '');
    setExtra(entry.extra || '');
    setExtraTotal(entry.extra_total?.toString() || '');
    setAbdulRehmanPayment(entry.abdul_rehman_payment?.toString() || '');
    setAdilMushtaqPayment(entry.adil_mushtaq_payment?.toString() || '');
    setUsmanYousafPayment(entry.usman_yousaf_payment?.toString() || '');
    setNotes(entry.notes || '');
    setEditingId(entry.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this expense entry?')) return;
    try {
      await api.delete(`/expenses/${id}/`);
      await fetchEntries();
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const monthLabel = currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Daily Expenses</h1>
          <p className="text-sm text-gray-500">Track office, panel, stock and other expenses</p>
        </div>
        {/* Month Navigation */}
        <div className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 px-3 py-2">
          <button onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1))} className="p-1 hover:bg-gray-100 rounded-lg">
            <ChevronLeft size={18} className="text-gray-600" />
          </button>
          <div className="flex items-center gap-2 px-3 py-1 min-w-[160px] justify-center">
            <Calendar size={16} className="text-blue-600" />
            <span className="text-sm font-semibold text-gray-800">{monthLabel}</span>
          </div>
          <button onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1))} className="p-1 hover:bg-gray-100 rounded-lg">
            <ChevronRight size={18} className="text-gray-600" />
          </button>
        </div>
      </div>

      {/* Entry Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 sm:p-6 mb-6">
        <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <Plus size={18} className="text-blue-600" />
          {editingId ? 'Edit Entry' : 'New Entry'}
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {/* Date */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" required />
          </div>

          {/* Provider Load */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Zong Load (PKR)</label>
            <input type="number" value={zongLoad} onChange={e => setZongLoad(e.target.value)}
              placeholder="e.g. 1000" step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Connect Load (PKR)</label>
            <input type="number" value={connectLoad} onChange={e => setConnectLoad(e.target.value)}
              placeholder="e.g. 2000" step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fibrish Load (PKR)</label>
            <input type="number" value={fibrishLoad} onChange={e => setFibrishLoad(e.target.value)}
              placeholder="e.g. 3000" step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">AsConTel Load (PKR)</label>
            <input type="number" value={ascontelLoad} onChange={e => setAscontelLoad(e.target.value)}
              placeholder="e.g. 4000" step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Load Total (PKR)</label>
            <div className="w-full px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg text-sm font-semibold text-blue-700">
              PKR {todayPanelLoadTotal.toLocaleString()}
            </div>
          </div>

          {/* Office Expense */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Office Expense (details)</label>
            <input type="text" value={officeExpense} onChange={e => setOfficeExpense(e.target.value)}
              placeholder="e.g. 1000 + 600 (petrol + lunch)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          {/* Office Total */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Office Total (PKR)</label>
            <input type="number" value={officeTotal} onChange={e => setOfficeTotal(e.target.value)}
              placeholder="e.g. 1600" step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          {/* Stock */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Stock (details)</label>
            <input type="text" value={stock} onChange={e => setStock(e.target.value)}
              placeholder="e.g. Fiber cable 500m"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          {/* Stock Total */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Stock Total (PKR)</label>
            <input type="number" value={stockTotal} onChange={e => setStockTotal(e.target.value)}
              placeholder="e.g. 15000" step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          {/* Extra */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Extra (details)</label>
            <input type="text" value={extra} onChange={e => setExtra(e.target.value)}
              placeholder="e.g. Worker salary"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          {/* Extra Total */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Extra Total (PKR)</label>
            <input type="number" value={extraTotal} onChange={e => setExtraTotal(e.target.value)}
              placeholder="e.g. 5000" step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          {/* Partner Payments Section Header */}
          <div className="sm:col-span-2 lg:col-span-3 border-t border-gray-200 pt-3 mt-2">
            <h3 className="text-xs font-bold text-gray-700 uppercase tracking-wide">Partner Payments</h3>
          </div>

          {/* Abdul Rehman Payment */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Abdul Rehman (PKR)</label>
            <input type="number" value={abdulRehmanPayment} onChange={e => setAbdulRehmanPayment(e.target.value)}
              placeholder="e.g. 10000" step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          {/* Adil Mushtaq Payment */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Adil Mushtaq (PKR)</label>
            <input type="number" value={adilMushtaqPayment} onChange={e => setAdilMushtaqPayment(e.target.value)}
              placeholder="e.g. 10000" step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          {/* Usman Yousaf Payment */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Usman Yousaf (PKR)</label>
            <input type="number" value={usmanYousafPayment} onChange={e => setUsmanYousafPayment(e.target.value)}
              placeholder="e.g. 10000" step="1"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>

          {/* Notes */}
          <div className="sm:col-span-2 lg:col-span-1">
            <label className="block text-xs font-medium text-gray-600 mb-1">Notes</label>
            <input type="text" value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Any additional notes"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" />
          </div>
        </div>

        <div className="mt-4 flex gap-2 justify-end">
          {editingId && (
            <button type="button" onClick={resetForm}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium">
              Cancel
            </button>
          )}
          <button type="submit" disabled={submitting}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium">
            {submitting ? <Loader size={16} className="animate-spin" /> : <Save size={16} />}
            {editingId ? 'Update Entry' : 'Save Entry'}
          </button>
        </div>
      </form>

      {/* Entries Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">Entries ({entries.length})</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center"><Loader className="animate-spin text-blue-600 mx-auto" size={24} /></div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No expense entries for {monthLabel}</div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Load</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Office</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Office Amt</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Stock</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Stock Amt</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Extra</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Extra Amt</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Partner Pmts</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Total</th>
                    <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {entries.map((entry) => (
                    <tr key={entry.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{entry.date}</td>
                      <td className="px-4 py-3 text-xs text-right text-gray-600">
                        <div>Zong: {parseFloat(entry.zong_load || 0).toLocaleString()}</div>
                        <div>Connect: {parseFloat(entry.connect_load || 0).toLocaleString()}</div>
                        <div>Fibrish: {parseFloat(entry.fibrish_load || 0).toLocaleString()}</div>
                        <div>AsConTel: {parseFloat(entry.ascontel_load || 0).toLocaleString()}</div>
                        <div className="font-bold text-gray-900">Total: {parseFloat(entry.panel_load || 0).toLocaleString()}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-[150px] truncate">{entry.office_expense || '-'}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{parseFloat(entry.office_total).toLocaleString()}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-[150px] truncate">{entry.stock || '-'}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{parseFloat(entry.stock_total).toLocaleString()}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 max-w-[150px] truncate">{entry.extra || '-'}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-900">{parseFloat(entry.extra_total).toLocaleString()}</td>
                      <td className="px-4 py-3 text-xs text-right">
                        {parseFloat(entry.partner_payments_total || 0) > 0 ? (
                          <div className="text-gray-700 font-semibold">
                            {parseFloat(entry.abdul_rehman_payment || 0) > 0 && <div>AR: {parseFloat(entry.abdul_rehman_payment).toLocaleString()}</div>}
                            {parseFloat(entry.adil_mushtaq_payment || 0) > 0 && <div>AM: {parseFloat(entry.adil_mushtaq_payment).toLocaleString()}</div>}
                            {parseFloat(entry.usman_yousaf_payment || 0) > 0 && <div>UY: {parseFloat(entry.usman_yousaf_payment).toLocaleString()}</div>}
                            <div className="text-gray-900 font-bold mt-1">Total: {parseFloat(entry.partner_payments_total).toLocaleString()}</div>
                          </div>
                        ) : <span className="text-gray-400">-</span>}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-gray-900">{parseFloat(entry.total).toLocaleString()}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleEdit(entry)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => handleDelete(entry.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden divide-y divide-gray-100">
              {entries.map((entry) => (
                <div key={entry.id} className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-gray-900">{entry.date}</span>
                    <span className="font-bold text-green-700">PKR {parseFloat(entry.total).toLocaleString()}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {parseFloat(entry.zong_load || 0) > 0 && (
                      <div><span className="text-gray-500">Zong:</span> <span className="font-medium">{parseFloat(entry.zong_load || 0).toLocaleString()}</span></div>
                    )}
                    {parseFloat(entry.connect_load || 0) > 0 && (
                      <div><span className="text-gray-500">Connect:</span> <span className="font-medium">{parseFloat(entry.connect_load || 0).toLocaleString()}</span></div>
                    )}
                    {parseFloat(entry.fibrish_load || 0) > 0 && (
                      <div><span className="text-gray-500">Fibrish:</span> <span className="font-medium">{parseFloat(entry.fibrish_load || 0).toLocaleString()}</span></div>
                    )}
                    {parseFloat(entry.ascontel_load || 0) > 0 && (
                      <div><span className="text-gray-500">AsConTel:</span> <span className="font-medium">{parseFloat(entry.ascontel_load || 0).toLocaleString()}</span></div>
                    )}
                    {parseFloat(entry.panel_load || 0) > 0 && (
                      <div><span className="text-gray-500">Load Total:</span> <span className="font-medium">{parseFloat(entry.panel_load || 0).toLocaleString()}</span></div>
                    )}
                    {parseFloat(entry.office_total) > 0 && (
                      <div><span className="text-gray-500">Office:</span> <span className="font-medium">{parseFloat(entry.office_total).toLocaleString()}</span></div>
                    )}
                    {parseFloat(entry.stock_total) > 0 && (
                      <div><span className="text-gray-500">Stock:</span> <span className="font-medium">{parseFloat(entry.stock_total).toLocaleString()}</span></div>
                    )}
                    {parseFloat(entry.extra_total) > 0 && (
                      <div><span className="text-gray-500">Extra:</span> <span className="font-medium">{parseFloat(entry.extra_total).toLocaleString()}</span></div>
                    )}
                    {parseFloat(entry.partner_payments_total || 0) > 0 && (
                      <div className="col-span-2 border-t border-gray-200 pt-1 mt-1">
                        <span className="text-gray-500">Partner Payments:</span>
                        {parseFloat(entry.abdul_rehman_payment || 0) > 0 && <div className="text-xs">AR: {parseFloat(entry.abdul_rehman_payment).toLocaleString()}</div>}
                        {parseFloat(entry.adil_mushtaq_payment || 0) > 0 && <div className="text-xs">AM: {parseFloat(entry.adil_mushtaq_payment).toLocaleString()}</div>}
                        {parseFloat(entry.usman_yousaf_payment || 0) > 0 && <div className="text-xs">UY: {parseFloat(entry.usman_yousaf_payment).toLocaleString()}</div>}
                      </div>
                    )}
                  </div>
                  {entry.office_expense && <p className="text-xs text-gray-500 mt-1">Office: {entry.office_expense}</p>}
                  {entry.stock && <p className="text-xs text-gray-500">Stock: {entry.stock}</p>}
                  <div className="flex gap-2 mt-2">
                    <button onClick={() => handleEdit(entry)} className="text-xs text-blue-600 font-medium">Edit</button>
                    <button onClick={() => handleDelete(entry.id)} className="text-xs text-red-600 font-medium">Delete</button>
                  </div>
                </div>
              ))}
            </div>

            {/* Monthly Summary */}
            <div className="bg-gray-50 border-t-2 border-gray-200 p-4 sm:p-6">
              <h3 className="font-semibold text-gray-800 mb-3 text-sm uppercase tracking-wide">Monthly Summary — {monthLabel}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500">Panel Load</p>
                  <div className="mt-1 space-y-0.5 text-xs text-gray-600">
                    <div>Zong: PKR {summary.zong_load_total.toLocaleString()}</div>
                    <div>Connect: PKR {summary.connect_load_total.toLocaleString()}</div>
                    <div>Fibrish: PKR {summary.fibrish_load_total.toLocaleString()}</div>
                    <div>AsConTel: PKR {summary.ascontel_load_total.toLocaleString()}</div>
                  </div>
                  <p className="text-lg font-bold text-gray-900 mt-1">PKR {summary.panel_load_total.toLocaleString()}</p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-blue-200">
                  <p className="text-xs text-blue-600">Today's Load</p>
                  <p className="text-lg font-bold text-blue-700">PKR {summary.today_load_total.toLocaleString()}</p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500">Office</p>
                  <p className="text-lg font-bold text-gray-900">PKR {summary.office_total.toLocaleString()}</p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500">Stock</p>
                  <p className="text-lg font-bold text-gray-900">PKR {summary.stock_total.toLocaleString()}</p>
                </div>
                <div className="bg-white p-3 rounded-lg border border-gray-200">
                  <p className="text-xs text-gray-500">Extra</p>
                  <p className="text-lg font-bold text-gray-900">PKR {summary.extra_total.toLocaleString()}</p>
                </div>
                <div className="bg-gradient-to-br from-red-500 to-red-600 p-3 rounded-lg text-white col-span-2 sm:col-span-5">
                  <p className="text-xs text-red-100">Total Expenses</p>
                  <p className="text-lg font-bold">PKR {summary.grand_total.toLocaleString()}</p>
                </div>
              </div>

              {/* Partner Payments Summary */}
              {Object.keys(summary.partner_limits || {}).length > 0 && (
                <div className="border-t border-gray-200 pt-4">
                  <h4 className="font-semibold text-gray-800 text-xs uppercase tracking-wide mb-3">Partner Payments & Limits</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {Object.entries(summary.partner_limits || {}).map(([key, data]) => (
                      <div key={key} className={`p-3 rounded-lg border ${data.status === 'reached' ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
                        <p className={`text-xs font-bold ${data.status === 'reached' ? 'text-red-700' : 'text-green-700'}`}>{data.name}</p>
                        <div className="mt-2 space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Paid:</span>
                            <span className="font-semibold text-gray-900">PKR {data.paid_this_month.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Limit:</span>
                            <span className="font-semibold text-gray-900">PKR {data.monthly_limit.toLocaleString()}</span>
                          </div>
                          <div className="flex justify-between border-t border-gray-200 pt-1">
                            <span className="text-gray-600">Remaining:</span>
                            <span className={`font-bold ${data.status === 'reached' ? 'text-red-700' : 'text-green-700'}`}>
                              PKR {data.remaining.toLocaleString()}
                            </span>
                          </div>
                        </div>
                        {data.status === 'reached' && (
                          <p className="text-xs text-red-700 font-bold mt-2 bg-red-100 rounded px-2 py-1">⚠️ Limit reached!</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Expenses;
